/**
 * Button Component
 *
 * Versatile button component with multiple variants and sizes.
 * Optimized for mobile touch targets (minimum 44x44px).
 */

import * as React from 'react';
import { cn } from '../../lib/utils';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'default' | 'destructive' | 'outline' | 'secondary' | 'ghost' | 'link';
  size?: 'default' | 'sm' | 'lg' | 'icon';
  asChild?: boolean;
}

/**
 * Helper function to get button variant class names
 */
export function buttonVariants({ variant = 'default', size = 'default' }: Partial<ButtonProps> = {}) {
  return cn(
    // Base styles
    'inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium',
    'transition-colors duration-200',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2',
    'disabled:pointer-events-none disabled:opacity-50',
    // Touch optimization
    'touch-manipulation active:scale-[0.98]',
    // Variant styles
    {
      // Default - Primary action
      'bg-primary-500 text-white hover:bg-primary-700': variant === 'default',
      // Destructive - Dangerous actions
      'bg-red-600 text-white hover:bg-red-700': variant === 'destructive',
      // Outline - Secondary action with border
      'border-2 border-gray-300 bg-transparent hover:bg-gray-100 text-gray-900':
        variant === 'outline',
      // Secondary - Alternative styling
      'bg-gray-200 text-gray-900 hover:bg-gray-300': variant === 'secondary',
      // Ghost - Minimal styling
      'hover:bg-gray-100 text-gray-900': variant === 'ghost',
      // Link - Text only
      'text-primary underline-offset-4 hover:underline': variant === 'link',
    },
    // Size styles - all meet 44px minimum for touch targets (WCAG 2.5.5)
    {
      'h-11 px-4 py-2 min-h-[44px]': size === 'default', // 44px minimum
      'h-11 rounded-md px-3 min-h-[44px]': size === 'sm', // 44px minimum
      'h-14 rounded-md px-8 min-h-[56px]': size === 'lg', // 56px for emphasis
      'h-11 w-11 min-h-[44px] min-w-[44px]': size === 'icon', // 44x44px minimum
    }
  );
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant = 'default', size = 'default', ...props }, ref) => {
    return (
      <button
        className={cn(buttonVariants({ variant, size }), className)}
        ref={ref}
        {...props}
      />
    );
  }
);
Button.displayName = 'Button';

export { Button };
